/**
 * The shop's details in one record (T01-16): cms-service `GET/PATCH /v1/admin/settings/store` —
 * name ja/en/カナ, 〒 + address, phone, e-mail, website, 公安委員会 + 古物商許可番号, logo.
 * Every signed-in staff member reads it (header); the owner (`settings.write`) saves it with
 * `If-Match`. Live (R21): `cms.store_settings.updated` reaches `staff:cms` (the Shop details form,
 * with the version) and `public:cms` (every header) — the queries below refetch within ≤ 2 s.
 */
import { useQuery } from "@tanstack/react-query";
import { type CmsPaths, createCmsClient } from "@tcg/api-client";
import { CMS_TOPIC } from "@tcg/realtime";
import { useMemo } from "react";

import { unwrap } from "@/lib/api/errors";
import { useServiceClient } from "@/lib/api/use-api";
import { useLiveInvalidate } from "@/realtime/hooks";

export type StoreSettingsOut =
  CmsPaths["/v1/admin/settings/store"]["get"]["responses"][200]["content"]["application/json"];
export type StoreSettingsPatch = NonNullable<
  CmsPaths["/v1/admin/settings/store"]["patch"]["requestBody"]
>["content"]["application/json"];

export const STORE_SETTINGS_KEY = ["settings", "store", "record"] as const;
/** The one record's id — `settingsId` of `cms.store_settings.updated` (the hub message id). */
const STORE_ID = "store";

export interface StoreSettingsApi {
  get(signal?: AbortSignal): Promise<StoreSettingsOut>;
  patch(body: StoreSettingsPatch, version: number): Promise<StoreSettingsOut>;
}

export function useStoreSettingsApi(): StoreSettingsApi {
  const cms = useServiceClient(createCmsClient);
  return useMemo(
    () => ({
      get: (signal) => unwrap(cms.GET("/v1/admin/settings/store", { signal })),
      patch: (body, version) =>
        unwrap(
          cms.PATCH("/v1/admin/settings/store", {
            params: { header: { "If-Match": `"${String(version)}"` } },
            body,
          }),
        ),
    }),
    [cms],
  );
}

/** The record for any signed-in staff member, kept live (header, receipt settings, Shop details). */
export function useStoreSettings(options: { enabled?: boolean } = {}) {
  const api = useStoreSettingsApi();
  // public:* messages carry no entity (T14-02) — the record's id is the constant "store"
  // (cms.store_settings.updated `settingsId`), so a page / theme publish does not refetch it
  const publicPoll = useLiveInvalidate(CMS_TOPIC, [STORE_SETTINGS_KEY], {
    match: (m) => m.entity === "store_settings" || (m.entity === undefined && m.id === STORE_ID),
    pollMs: 5 * 60_000,
  });
  // staff:cms (cms.edit) carries the record's version — the Shop details form's conflict bar
  useLiveInvalidate("staff:cms", [STORE_SETTINGS_KEY], {
    match: (m) => m.entity === "store_settings",
  });
  return useQuery({
    queryKey: STORE_SETTINGS_KEY,
    enabled: options.enabled ?? true,
    meta: { silent: true },
    retry: false,
    staleTime: 60_000,
    refetchInterval: publicPoll,
    queryFn: ({ signal }) => api.get(signal),
  });
}

/** Name for the UI language: ja → ja (else en); other languages → en (else ja). */
export function storeName(settings: StoreSettingsOut | undefined, language: string | undefined) {
  const ja = settings?.name?.ja?.trim() || null;
  const en = settings?.name?.en?.trim() || null;
  return (language === "ja" ? (ja ?? en) : (en ?? ja)) ?? null;
}

export function storeLogoUrl(settings: StoreSettingsOut | undefined): string | null {
  const logo = settings?.logo;
  return logo ? (logo.thumbnailUrl ?? logo.url ?? null) : null;
}
